import { useEffect, useRef, useState } from 'react'
import { useSocialStore } from '@/store/social'
import { useVoiceStore } from '@/store/voice'
import { describeError } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { desktopPlatform } from '@/lib/desktop'
import { describeMediaError } from '@/lib/mediaErrors'
import { MAX_VOICE_SECONDS, startVoiceRecording, uploadVoice, type VoiceRecording } from '@/lib/voiceMessage'

/**
 * Запись голосового из композера: микрофон из настроек голоса, полоса «идёт запись» с временем и
 * живой волной, «Отправить» и «Отмена». Через 20 минут запись отправляется сама.
 */
export function useVoiceRecorder(channelId: string, onError: (message: string | null) => void) {
  const [rec, setRec] = useState<VoiceRecording | null>(null)
  const [busy, setBusy] = useState(false)
  const [, setTick] = useState(0)
  const recRef = useRef<VoiceRecording | null>(null)

  const send = async () => {
    const r = recRef.current
    if (!r) return
    recRef.current = null
    setBusy(true)
    try {
      const clip = await r.stop()
      setRec(null)
      if (clip.seconds < 0.6) {
        onError('Слишком короткое голосовое')
        return
      }
      const ref = await uploadVoice(channelId, clip)
      await useSocialStore.getState().sendVoice(channelId, ref)
    } catch (err) {
      setRec(null)
      onError(err instanceof Error && !(err instanceof ApiError) ? err.message : describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  // время и волна перерисовываются по ходу записи; на пределе запись уходит сама
  useEffect(() => {
    if (!rec) return
    const t = setInterval(() => {
      setTick((n) => n + 1)
      if (rec.elapsed() >= MAX_VOICE_SECONDS) void send()
    }, 100)
    return () => clearInterval(t)
  })

  // ушли из канала — запись отменяется, микрофон отпускается
  useEffect(() => () => recRef.current?.cancel(), [channelId])

  const start = async () => {
    if (recRef.current || busy) return
    const p = useVoiceStore.getState().prefs
    try {
      const r = await startVoiceRecording({
        ...(p.audioinput ? { deviceId: { ideal: p.audioinput } } : {}),
        echoCancellation: p.echoCancellation, noiseSuppression: p.noiseSuppression, autoGainControl: p.autoGainControl,
      })
      recRef.current = r
      setRec(r)
      onError(null) // прошлая ошибка микрофона больше не про эту запись
    } catch (err) {
      onError(describeMediaError(err, 'mic', desktopPlatform))
    }
  }

  const cancel = () => {
    recRef.current?.cancel()
    recRef.current = null
    setRec(null)
  }

  return { rec, busy, recording: Boolean(rec), start, send, cancel }
}
